import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  CreateSlaPolicyRequest,
  ListResponse,
  ReorderSlaPoliciesRequest,
  SlaPolicyQuery,
  SlaPolicyResponse,
  UpdateSlaPolicyRequest,
} from '@troia-v3/types';

import { slaPoliciesApi } from '../services/slaPoliciesApi';
import { sortPoliciesForEvaluation } from '../utils/slaPolicyHelpers';

const QUERY_KEY = 'sla-policies';

export const SLA_POLICY_KEYS = {
  all: [QUERY_KEY] as const,
  list: (query?: SlaPolicyQuery) => [QUERY_KEY, 'list', query] as const,
  detail: (id: string) => [QUERY_KEY, 'detail', id] as const,
};

/** Mensagem do servidor quando existir; senão o texto padrão da ação. */
const errorMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

/**
 * `select` ordena `data.items` na ordem de AVALIAÇÃO do motor
 * (`sortPoliciesForEvaluation` — `order asc, id asc`, mesmo desempate do
 * backend) antes de entregar aos consumidores. **CAMADA DE DADOS, um lugar
 * só**: nem esta hook nem `slaPoliciesApi.list` passam `sort` pro
 * `GET /api/sla-policies`, e o `GenericRepository.buildSort` do servidor
 * defaulta pra `{ createdAt: -1 }` sem um `sort` explícito — sem esta linha
 * a listagem chegava na ordem de CRIAÇÃO, não na de avaliação (achado da
 * Rodada 1 de conserto, C21). `SlaPoliciesPage` (C17, ordem da tabela +
 * `rankPoliciesForDisplay`) e `PipelinesSettingsDrawer` (C21,
 * `linkedSlaPolicy = .find()`) consomem esta hook e herdam a correção sem
 * precisar de mudança própria — ver `sortPoliciesForEvaluation` para o
 * raciocínio completo (inclusive por que políticas inativas/de outro
 * `entityType` não precisam de tratamento especial).
 *
 * **`query === undefined` DESLIGA o fetch** (`enabled`), e é assim que os dois
 * consumidores fazem o gate que seus comentários declaram (CLAUDE.md NUNCA
 * #14): `PipelinesSettingsDrawer` monta sempre (o `TicketsPage` o mantém
 * montado pela animação de saída) e só monta a query com `isOpen &&
 * canLinkSlaPolicy`; `SlaPoliciesPage` só monta com `sla-policies:read`. Sem
 * o `enabled`, `slaPoliciesApi.list(undefined)` dispara o `GET` do mesmo
 * jeito (params `undefined` não impedem a requisição) — em toda montagem da
 * página de chamados, e devolvendo 403 em segundo plano para quem não tem a
 * permissão (achado da revisão final ampla, 15/08).
 */
export const useSlaPolicies = (query?: SlaPolicyQuery) =>
  useQuery<ListResponse<SlaPolicyResponse>, Error, ListResponse<SlaPolicyResponse>>({
    queryKey: SLA_POLICY_KEYS.list(query),
    queryFn: () => slaPoliciesApi.list(query),
    enabled: query !== undefined,
    staleTime: 30_000,
    select: (data) => ({ ...data, items: sortPoliciesForEvaluation(data.items) }),
  });

export const useCreateSlaPolicy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSlaPolicyRequest) => slaPoliciesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SLA_POLICY_KEYS.all });
      toast.success('Política criada com sucesso!');
    },
    onError: (error: unknown) => toast.error(errorMessage(error, 'Erro ao criar política')),
  });
};

/**
 * `options.silent` (Rodada 1 de conserto, C21): suprime os DOIS toasts
 * automáticos (sucesso e erro) sem tocar a invalidação de cache. Existe
 * porque `PipelinesSettingsDrawer.syncSlaPolicyLink` faz até DOIS `PUT`
 * nesta mutation (tira do escopo antigo, põe no novo) para UM clique de
 * "Salvar" — sem `silent`, um clique podia gerar até 3 toasts (o do
 * pipeline + os 2 desta mutation), e o toast genérico de erro
 * ("Erro ao atualizar política") não distingue "PUT1 falhou, nada mudou" de
 * "PUT2 falhou depois do PUT1 ter sido aplicado, o vínculo sumiu" — só o
 * chamador sabe qual dos dois aconteceu e qual mensagem/estado mostrar.
 * Consumidores que editam a política DIRETAMENTE (`SlaPolicyDrawer`, C16)
 * continuam com o comportamento padrão (`silent` ausente = `false`).
 */
export const useUpdateSlaPolicy = (options?: { silent?: boolean }) => {
  const silent = options?.silent ?? false;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSlaPolicyRequest }) =>
      slaPoliciesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SLA_POLICY_KEYS.all });
      if (!silent) toast.success('Política atualizada com sucesso!');
    },
    onError: (error: unknown) => {
      if (!silent) toast.error(errorMessage(error, 'Erro ao atualizar política'));
    },
  });
};

export const useDeleteSlaPolicy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => slaPoliciesApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SLA_POLICY_KEYS.all });
      toast.success('Política excluída com sucesso!');
    },
    onError: (error: unknown) => toast.error(errorMessage(error, 'Erro ao excluir política')),
  });
};

/**
 * UMA chamada para a lista inteira: `POST /sla-policies/reorder` grava `order`
 * a partir da posição no array e devolve a lista já reordenada; um arrasto =
 * um toast.
 *
 * Contraste com o arrasto de etapas (`StagesManager`, C7), que ainda dispara N
 * `PUT` (um por etapa, um toast por etapa) — **não por falta de rota**: o
 * `POST /ticket-stages/reorder` existe ponta a ponta (router do backend +
 * `pipelinesApi.reorderStages` + `useReorderStages`, hoje exportado e sem
 * nenhum consumidor). Trocar os N `PUT` pelo endpoint é backlog declarado do
 * plano — mudança de comportamento no arrasto de etapas, com teste próprio,
 * fora do escopo desta tela.
 *
 * **`data.policyIds` tem que ser o conjunto COMPLETO de políticas ATIVAS do
 * `entityType` sendo reordenado, nunca um subconjunto.** Contrato confirmado
 * em `SlaPoliciesService.reorder` (backend, rodada B onda 2): o servidor
 * cruza a lista recebida contra `findActiveForCompany` (o mesmo universo que
 * a resolução de SLA percorre) e devolve **400** nomeando os ids que
 * faltaram ou que não pertencem ao conjunto — não completa nem ignora a
 * diferença em silêncio, porque uma lista parcial reposicionaria TODAS as
 * políticas ativas (a diferença de `order` entre vizinhos que ficaram de
 * fora seria perdida). O chamador (tela de políticas) deve montar
 * `policyIds` a partir da listagem inteira do `entityType` corrente — nunca
 * de uma página ou de uma seleção filtrada.
 */
export const useReorderSlaPolicies = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReorderSlaPoliciesRequest) => slaPoliciesApi.reorder(data),
    // `onSuccess` PRECISA retornar a promise do `invalidateQueries`: o
    // TanStack Query aguarda o retorno do callback antes de assentar
    // `isPending`/`isSuccess` (docs: "if you return a promise... it will
    // first be awaited"). Sem o `return`, `isPending` cai assim que o POST
    // /reorder volta — ANTES do refetch da listagem terminar — e o
    // `SlaPoliciesPage` usa exatamente esse flag pra saber quando descartar
    // a ordem local do arrasto (`if (!reorderPolicies.isPending)
    // setDragOrder(null)`). O efeito visível era a linha "voltar" pra ordem
    // antiga por uma fração de segundo e "pular" de novo quando o refetch
    // enfim chegava — achado da Rodada 1 de revisão (C17+C18).
    onSuccess: () => {
      toast.success('Ordem de avaliação atualizada');
      return queryClient.invalidateQueries({ queryKey: SLA_POLICY_KEYS.all });
    },
    onError: (error: unknown) => toast.error(errorMessage(error, 'Erro ao reordenar políticas')),
  });
};

export const useSetDefaultSlaPolicy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => slaPoliciesApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SLA_POLICY_KEYS.all });
      toast.success('Política padrão definida');
    },
    onError: (error: unknown) => toast.error(errorMessage(error, 'Erro ao definir a política padrão')),
  });
};

/**
 * Busca UMA política FRESCA (`staleTime: 0`, nunca do cache de 30s de
 * `useSlaPolicies`) — mesmo padrão de `usePipelineDeleteValidation`
 * (`helpdesk/pipelines/hooks/usePipelines.ts`, C8): `fetchQuery` em vez de
 * `useQuery` porque a pergunta só existe no momento da escrita, não é algo
 * pra manter montado.
 *
 * Rodada 1 de conserto (C21): `PipelinesSettingsDrawer.syncSlaPolicyLink`
 * espalhava `...linkedSlaPolicy.scope` a partir do cache de 30s da listagem
 * — duas abas (ou a própria tela de Políticas) editando o `scope` da mesma
 * política na janela de 30s faziam a escrita mais lenta apagar a mudança da
 * mais rápida (last-write-wins). Buscar fresco ANTES de cada `PUT` estreita
 * essa janela para o round-trip da própria requisição; não elimina — CAS de
 * verdade é responsabilidade do backend (`SlaPoliciesService.update` não
 * versiona `scope` hoje), registrado como residual conhecido.
 *
 * Lança quando a rota falha; o chamador decide o que fazer sem o veredito.
 */
export const useFetchFreshSlaPolicy = () => {
  const queryClient = useQueryClient();
  return useCallback(
    (id: string): Promise<SlaPolicyResponse> =>
      queryClient.fetchQuery({
        queryKey: SLA_POLICY_KEYS.detail(id),
        queryFn: () => slaPoliciesApi.getById(id),
        staleTime: 0,
      }),
    [queryClient],
  );
};
